'use client';
import { Content } from '.generated/Feature.CW.model';

import ButtonGroup, { ButtonGroupProps } from 'helpers/SitecoreWrappers/ButtonGroup/ButtonGroup';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { tv } from 'tailwind-variants';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import useIsEditing from 'lib/hooks/useIsEditing';
import { EditingHelpText } from 'helpers/Editing/EditingHelpText';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import classNames from 'classnames';
import { JSX } from 'react';

export type TextMediaProps = Content.TextMedia &
  ButtonGroupProps & {
    params: {
      Theme: string;
    };
    isSidenavPart?: boolean;
  };

export const MediaSection = (props: TextMediaProps): JSX.Element => {
  const { imageClass, videoClass } = TailwindVariants();
  const isEditing = useIsEditing();

  if (isEditing && props?.fields?.videoID?.value !== '') {
    return (
      <div className="component">
        <div className="max-w-7xl w-full overflow-hidden">
          <EditingHelpText>
            The Video Media Componet will not be shown in page builder. Only use External links for
            Video.
          </EditingHelpText>
          <div className="flex flex-wrap items-center gap-5 p-4">
            <span className="text-18 font-bold">Wistia Video Embed URL :- </span>
            <PlainTextWrapper field={props.fields?.videoID} />
          </div>
        </div>
      </div>
    );
  }

  if (!props.fields) return <></>;

  return (
    <>
      <div
        className={`md:w-1/2 ${props?.fields?.hideImageOnMobile?.value ? 'hidden md:block' : 'block'}`}
      >
        {props?.fields?.videoID?.value !== '' ? (
          <div
            role="region"
            aria-labelledby="Wistia video player container"
            className={videoClass()}
          >
            <WistiaPlayer
              mediaId={(props?.fields?.videoID?.value as string) || ''}
              controlsVisibleOnLoad={false}
              endVideoBehavior="reset"
            />
          </div>
        ) : (
          <ImageWrapper field={props?.fields?.image} className={imageClass()} />
        )}
      </div>
    </>
  );
};

export const ContentSection = (props: TextMediaProps): JSX.Element => {
  const { titleClass, descriptionText, buttonWrapperClass } = TailwindVariants();
  const isEditing = useIsEditing();

  if (!props.fields) return <></>;

  return (
    <div className="md:w-1/2">
      {(props?.fields?.title?.value !== '' || isEditing) &&
        (() => {
          const HeadingTag = props?.isSidenavPart ? 'h3' : 'h2';
          return (
            <HeadingTag
              className={classNames(
                titleClass(),
                props?.isSidenavPart
                  ? 'text-24 leading-28 lg:text-26 lg:leading-31'
                  : 'text-[30px] leading-[36px] lg:text-36 lg:leading-43'
              )}
            >
              <PlainTextWrapper field={props?.fields?.title} />
            </HeadingTag>
          );
        })()}

      <RichTextWrapper field={props?.fields?.description} className={descriptionText()} />
      <ButtonGroup
        {...props}
        WrapperClasses="flex-col mt-[32px] mb-2"
        ButtonWrapperClasses={buttonWrapperClass()}
      />
    </div>
  );
};

// Default variant will be Image on the Right and Content on the left
export const Default = (props: TextMediaProps): JSX.Element => {
  const { wrapper } = TailwindVariants();

  const isEditingData = useIsEditing();

  if (props?.fields?.description?.value === '' && !isEditingData) {
    return <></>;
  }

  return (
    <ComponentWrapper
      params={props?.params}
      isSidenavPart={props?.isSidenavPart}
      className="bg-theme-bg"
    >
      {/* Wrapper  */}
      <div className={`${wrapper()} flex-col`}>
        {/* Content Section */}
        <ContentSection {...props} />
        {/* Media section */}
        <MediaSection {...props} />
      </div>
    </ComponentWrapper>
  );
};

export const ImageLeft = (props: TextMediaProps) => {
  const { wrapper } = TailwindVariants();
  const isEditingData = useIsEditing();

  if (props?.fields?.description?.value === '' && !isEditingData) {
    return <></>;
  }
  return (
    <ComponentWrapper
      params={props?.params}
      isSidenavPart={props?.isSidenavPart}
      className="bg-theme-bg"
    >
      {/* Wrapper  */}
      <div className={`${wrapper()} flex-col-reverse`}>
        {/* Media section */}
        <MediaSection {...props} />

        {/* Content Section */}
        <ContentSection {...props} />
      </div>
    </ComponentWrapper>
  );
};

export const ImageRight = (props: TextMediaProps) => {
  return <Default {...props} />;
};

const TextMedia = (props: TextMediaProps): JSX.Element => {
  return <Default {...props} />;
};
export default TextMedia;

export const TailwindVariants = tv({
  slots: {
    wrapper:
      'mx-auto max-w-screen-xxl lg:px-20 px-10 flex md:flex-row w-full order-last md:order-first gap-16 md:gap-[48px]',
    titleClass: 'font-centuryGothic text-theme-text font-bold mb-8',
    descriptionText: 'font-nunitoSans text-16 leading-25 font-[500] text-theme-text',
    imageClass:
      'w-full h-full object-cover object-center aspect-[3/2] md:rounded-lg rounded-md object-cover object-center aspect-[3/2]',
    videoClass: 'md:rounded-lg rounded-md overflow-hidden aspect-video',
    buttonWrapperClass: 'md:mb-[24px] mb-6 md:!gap-12 !gap-6 md:flex-row flex-col',
  },
});
